import { useCallback, useEffect, useState } from "react";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { API } from "./apiClient";

const f = (value, fallback = 0) => {
  try {
    return typeof value === "number" ? value : (parseFloat(value) || fallback);
  } catch {
    return fallback;
  }
};

function round(value, digits = 2) {
  return Math.round(value * (10 ** digits)) / (10 ** digits);
}

function Card({ title, children, className = "" }) {
  return (
    <div className={`bg-gray-900 border border-gray-800 rounded-xl p-5 ${className}`}>
      {title ? <h3 className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-3">{title}</h3> : null}
      {children}
    </div>
  );
}

const SECTOR_COLORS = {
  simultaneous: "#10b981",
  resolution: "#3b82f6",
  adr_arbitrage: "#f59e0b",
  crypto_arbitrage: "#8b5cf6",
  etf_nav_arbitrage: "#ec4899",
  economic_arbitrage: "#06b6d4",
};

const SECTOR_LABELS = {
  simultaneous: "Simultaneo",
  resolution: "Resolucion",
  adr_arbitrage: "ADRs LATAM",
  crypto_arbitrage: "Crypto",
  etf_nav_arbitrage: "ETFs NAV",
  economic_arbitrage: "Economico",
};

const STATUS_COLORS = {
  open: "text-yellow-400",
  guaranteed_open: "text-yellow-400",
  closed: "text-green-400",
  guaranteed_closed: "text-green-400",
  closed_early: "text-blue-400",
  execution_failed: "text-red-400",
  expired: "text-gray-500",
  duplicate: "text-gray-700",
};

function formatEcTradeTime(value) {
  if (!value) return "--:--";
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(text)) return text.slice(11, 16);
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleTimeString("es-EC", {
      timeZone: "America/Guayaquil",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return text.length >= 5 ? text.slice(-5) : text;
}

function ProgressBar({ value, max, color = "bg-green-500", height = "h-3" }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const over = value > max && max > 0;
  return (
    <div className={`w-full bg-gray-800 rounded-full overflow-hidden ${height}`}>
      <div
        className={`h-full rounded-full transition-all duration-700 ${over ? "bg-gradient-to-r from-yellow-500 to-green-400" : color}`}
        style={{ width: `${Math.min(100, pct)}%` }}
      />
    </div>
  );
}

function money(value) {
  return `$${f(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function App() {
  const [status, setStatus] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [trades, setTrades] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterStrategy, setFilterStrategy] = useState("");
  const [tradesDirty, setTradesDirty] = useState(false);

  const fetchTrades = useCallback(async (from, to, strategy) => {
    let params = "?";
    if (from) params += `date_from=${from}&`;
    if (to) params += `date_to=${to}&`;
    if (strategy) params += `strategy=${strategy}`;
    try {
      const payload = await API.getTrades(params);
      if (Array.isArray(payload)) setTrades(payload);
    } catch (error) {
      console.error("No se pudieron actualizar los trades", error);
    } finally {
      setTradesDirty(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [statusResult, sectorsResult, historyResult] = await Promise.allSettled([
        API.getStatus(),
        API.getSectors(),
        API.getPnLHistory(),
      ]);

      if (statusResult.status === "fulfilled") setStatus(statusResult.value);
      else console.error("No se pudo actualizar status", statusResult.reason);

      if (sectorsResult.status === "fulfilled") setSectors(Array.isArray(sectorsResult.value) ? sectorsResult.value : []);
      else console.error("No se pudo actualizar sectores", sectorsResult.reason);

      if (historyResult.status === "fulfilled") setHistory(Array.isArray(historyResult.value) ? historyResult.value : []);
      else console.error("No se pudo actualizar historial", historyResult.reason);

      setLastUpdate(new Date().toLocaleTimeString("es-EC", { timeZone: "America/Guayaquil" }));
      if (!tradesDirty) await fetchTrades(dateFrom, dateTo, filterStrategy);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filterStrategy, fetchTrades, tradesDirty]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-gray-400 text-lg">Cargando...</div>
      </div>
    );
  }

  const mode = (status?.modo || "paper").toLowerCase();
  const pnlReferencia = f(status?.pnl_referencia_objetivo, f(status?.pnl_periodo));
  const pnlRealizadoLive = f(status?.pnl_realizado_live);
  const pnlAbiertoMark = f(status?.pnl_abierto_mark_to_exit);
  const pnlModelado = f(status?.pnl_modelado_auditado, f(status?.pnl_desde_revision));
  const pnlBrutoRevision = f(status?.pnl_bruto_desde_revision, pnlModelado);
  const recorteRevision = f(status?.recorte_desde_revision);
  const auditStart = status?.periodo_auditado_inicio || status?.periodo_inicio || "2026-04-13";

  const objetivoUtilidad = f(status?.objetivo_utilidad_ciclo, f(status?.objetivo_minimo, 8500));
  const objetivoEquity = f(status?.objetivo_equity_ciclo, 10000);
  const capital = f(status?.capital_total, 1500);
  const capitalDesplegable = f(status?.capital_desplegable, capital);
  const capitalComprometido = f(status?.capital_comprometido);
  const capitalDisponible = f(status?.capital_disponible_real);
  const reservaUsd = f(status?.reserva_usd);
  const reservaPct = f(status?.reserva_pct);
  const overcapUsd = f(status?.capital_sobrecap_usd);
  const gapMes = Math.max(0, objetivoUtilidad - pnlReferencia);
  const progreso = objetivoUtilidad > 0 ? (pnlReferencia / objetivoUtilidad) * 100 : 0;
  const objetivoAlcanzado = progreso >= 100;
  const historyHint = mode === "live"
    ? "Barras diarias de PnL realizado live del ciclo."
    : "Barras diarias de referencia auditada mientras seguimos en paper.";

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Billuzo</h1>
            <p className="text-gray-500 text-sm">Arbitrage Engine - {(status?.modo || "paper").toUpperCase()}</p>
          </div>
          <div className="flex items-center gap-3">
            {objetivoAlcanzado ? (
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-900 text-green-300 animate-pulse">OBJETIVO ALCANZADO</span>
            ) : null}
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${mode === "live" ? "bg-green-900 text-green-300" : "bg-blue-900 text-blue-300"}`}>
              {(status?.modo || "paper").toUpperCase()}
            </span>
            <span className="text-gray-600 text-xs">{lastUpdate}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
          <Card>
            <p className="text-gray-500 text-xs mb-1">{mode === "live" ? "PnL realizado live" : "PnL de referencia"}</p>
            <p className={`text-2xl font-bold ${objetivoAlcanzado ? "text-yellow-400" : "text-green-400"}`}>
              {money(pnlReferencia)}
            </p>
            <p className="text-gray-500 text-[11px] mt-1">
              {mode === "live"
                ? "Caja cerrada con fills live confirmados."
                : "Referencia de mayo basada en PnL auditado/modelado."}
            </p>
            <p className="text-gray-600 text-[11px] mt-1">Ciclo: {status?.periodo_inicio || "--"} -&gt; {status?.periodo_fin || "--"}</p>
            <p className="text-gray-600 text-[11px] mt-1">Audit window: {auditStart} -&gt; hoy</p>
            <p className="text-gray-700 text-[11px] mt-2">El headline separa live, abierto mark-to-exit y modelado auditado.</p>
          </Card>

          <Card>
            <p className="text-gray-500 text-xs mb-1">Split de PnL</p>
            <p className={`text-2xl font-bold ${pnlRealizadoLive >= 0 ? "text-green-400" : "text-red-400"}`}>{money(pnlRealizadoLive)}</p>
            <p className="text-gray-600 text-xs mt-1">Realizado live confirmado</p>
            <div className="mt-3 pt-3 border-t border-gray-800 space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Abierto mark-to-exit</span>
                <span className={pnlAbiertoMark >= 0 ? "text-blue-400" : "text-red-400"}>{money(pnlAbiertoMark)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Modelado auditado</span>
                <span className={pnlModelado >= 0 ? "text-yellow-400" : "text-red-400"}>{money(pnlModelado)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Bruto auditado</span>
                <span className="text-gray-400">{money(pnlBrutoRevision)}</span>
              </div>
              {recorteRevision > 0 ? (
                <p className="text-gray-700 text-[11px]">Recorte desde revision: {money(recorteRevision)}</p>
              ) : null}
            </div>
          </Card>

          <Card>
            <p className="text-gray-500 text-xs mb-1">Objetivo del Ciclo</p>
            <p className={`text-2xl font-bold ${objetivoAlcanzado ? "text-green-400" : "text-yellow-400"}`}>{money(gapMes)}</p>
            <p className="text-gray-600 text-xs mt-1">Gap a utilidad objetivo de {money(objetivoUtilidad)}</p>
            <div className="mt-3 pt-3 border-t border-gray-800 space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Equity objetivo</span>
                <span className="text-gray-300">{money(objetivoEquity)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Equity realizada</span>
                <span className="text-green-400">{money(status?.equity_realizada_ciclo)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Equity marcada</span>
                <span className="text-blue-400">{money(status?.equity_marcada_ciclo)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Goal diario</span>
                <span className="text-orange-400">{money(status?.goal_dia)}</span>
              </div>
            </div>
          </Card>

          <Card>
            <p className="text-gray-500 text-xs mb-1">Capital Operativo</p>
            <p className="text-2xl font-bold text-white">{money(capital)}</p>
            <p className="text-gray-600 text-xs mt-1">Capital desplegable: {money(capitalDesplegable)}</p>
            <div className="mt-2 pt-2 border-t border-gray-800">
              <p className="text-gray-500 text-xs mb-0.5">Capital asignado</p>
              <p className="text-lg font-bold text-blue-400">{money(capitalComprometido)}</p>
              <p className="text-gray-600 text-xs">
                {capitalDesplegable > 0 ? round((capitalComprometido / capitalDesplegable) * 100, 1).toFixed(1) : "0.0"}% del capital desplegable
              </p>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-800">
              <p className="text-gray-500 text-xs mb-0.5">Capital disponible</p>
              <p className="text-lg font-bold text-emerald-400">{money(capitalDisponible)}</p>
              <p className="text-gray-600 text-xs">
                {capitalDesplegable > 0 ? round((capitalDisponible / capitalDesplegable) * 100, 1).toFixed(1) : "0.0"}% del capital desplegable
              </p>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-800">
              <p className="text-gray-500 text-xs mb-0.5">Reserva / buffer</p>
              <p className="text-base font-bold text-gray-400">
                {money(reservaUsd)} <span className="text-gray-600 text-xs font-normal">({reservaPct.toFixed(1)}%)</span>
              </p>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-800">
              <p className="text-gray-500 text-xs mb-0.5">Overcap</p>
              <p className={`text-base font-bold ${overcapUsd > 0 ? "text-red-400" : "text-gray-400"}`}>{money(overcapUsd)}</p>
            </div>
          </Card>

          <Card>
            <p className="text-gray-500 text-xs mb-1">Riesgo Operativo</p>
            <p className={`text-2xl font-bold ${f(status?.dias_restantes) <= 3 ? "text-red-400" : "text-blue-400"}`}>
              {status?.dias_restantes || 0}
            </p>
            <p className="text-gray-600 text-xs mt-1">dias restantes | {status?.open_trades} posiciones abiertas</p>
            <div className="mt-3 pt-3 border-t border-gray-800 space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Trades cerrados</span>
                <span className="text-gray-300">{status?.closed_trades || 0}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Limite abiertos</span>
                <span className="text-gray-300">{status?.max_total_open_trades ?? "--"}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Cap max total</span>
                <span className="text-gray-300">{money(status?.max_total_open_capital_usd)}</span>
              </div>
            </div>
          </Card>
        </div>

        <Card className="mb-6">
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">Progreso del ciclo a utilidad objetivo</span>
                <span className={`font-bold ${objetivoAlcanzado ? "text-yellow-400" : "text-white"}`}>
                  {progreso.toFixed(1)}% {objetivoAlcanzado ? "OK" : ""}
                </span>
              </div>
              <ProgressBar value={pnlReferencia} max={objetivoUtilidad} color="bg-green-500" height="h-4" />
              <div className="flex justify-between text-xs text-gray-600 mt-1">
                <span>$0</span>
                <span>{money(pnlReferencia)} / {money(objetivoUtilidad)}</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">Progreso del dia vs goal diario</span>
                <span className={`font-bold ${f(status?.pnl_hoy_pct) >= 100 ? "text-green-400" : "text-gray-300"}`}>{f(status?.pnl_hoy_pct).toFixed(1)}%</span>
              </div>
              <ProgressBar value={Math.max(0, f(status?.pnl_hoy))} max={f(status?.goal_dia, 274)} color="bg-blue-500" height="h-2" />
              <div className="flex justify-between text-xs text-gray-600 mt-1">
                <span>{f(status?.pnl_hoy) < 0 ? `${money(status?.pnl_hoy)} perdida hoy` : "$0"}</span>
                <span>{money(Math.max(0, f(status?.pnl_hoy)))} / {money(status?.goal_dia)} goal</span>
              </div>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card title="PnL Diario Defendible">
            <p className="text-gray-600 text-xs mb-3">{historyHint}</p>
            {history.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={history}>
                  <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 10 }} tickLine={false} tickFormatter={(value) => String(value).slice(5)} />
                  <YAxis tick={{ fill: "#6b7280", fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value.toFixed(0)}`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#111827", border: "1px solid #374151", borderRadius: "8px", color: "#ffffff" }}
                    itemStyle={{ color: "#ffffff" }}
                    labelStyle={{ color: "#9ca3af" }}
                    formatter={(value) => [`$${f(value).toFixed(2)}`, "PnL diario"]}
                  />
                  <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                    {history.map((entry, index) => (
                      <Cell key={index} fill={f(entry.pnl) >= 0 ? "#10b981" : "#ef4444"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-600 text-sm text-center py-8">Sin datos historicos</p>
            )}
          </Card>

          <Card title="PnL por Sector (Operativo)">
            <p className="text-gray-600 text-xs mb-3">Vista completa del sistema para monitoreo. El headline principal se mantiene separado.</p>
            {sectors.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={sectors} layout="vertical">
                  <XAxis type="number" tick={{ fill: "#6b7280", fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value.toFixed(0)}`} />
                  <YAxis type="category" dataKey="strategy" tick={{ fill: "#6b7280", fontSize: 9 }} tickLine={false} width={90} tickFormatter={(value) => SECTOR_LABELS[value] || value} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#111827", border: "1px solid #374151", borderRadius: "8px", color: "#ffffff" }}
                    itemStyle={{ color: "#ffffff" }}
                    labelStyle={{ color: "#9ca3af" }}
                    formatter={(value) => [`$${f(value).toFixed(2)}`, "PnL"]}
                    labelFormatter={(label) => SECTOR_LABELS[label] || label}
                  />
                  <Bar dataKey="pnl" radius={[0, 4, 4, 0]}>
                    {sectors.map((sector, index) => (
                      <Cell key={index} fill={SECTOR_COLORS[sector.strategy] || "#6b7280"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-600 text-sm text-center py-8">Sin datos</p>
            )}
          </Card>
        </div>

        <Card title="Sectores Activos - Vista Operativa Completa" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
            {sectors.map((sector) => {
              const pct = f(sector.pct_asignado);
              const capitalSector = f(sector.capital_comprometido);
              return (
                <div key={sector.strategy} className="bg-gray-800 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: SECTOR_COLORS[sector.strategy] || "#6b7280" }} />
                      <span className="text-xs text-gray-300 font-medium">{SECTOR_LABELS[sector.strategy] || sector.strategy}</span>
                    </div>
                    <span className="text-xs font-bold text-blue-400">{pct}%</span>
                  </div>
                  <p className={`text-lg font-bold ${sector.pnl > 0 ? "text-green-400" : sector.pnl < 0 ? "text-red-400" : "text-gray-400"}`}>
                    {money(sector.pnl)}
                  </p>
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-600 text-xs">{sector.open} abier / {sector.closed} cerr</span>
                    <span className="text-gray-500 text-xs">{money(capitalSector)}</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-1 mt-2">
                    <div className="h-1 rounded-full" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: SECTOR_COLORS[sector.strategy] || "#6b7280" }} />
                  </div>
                </div>
              );
            })}

            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-gray-500" />
                  <span className="text-xs text-gray-400 font-medium">Reserva</span>
                </div>
                <span className="text-xs font-bold text-gray-400">{reservaPct.toFixed(1)}%</span>
              </div>
              <p className="text-lg font-bold text-gray-400">{money(reservaUsd)}</p>
              <div className="flex justify-between mt-1">
                <span className="text-gray-600 text-xs">Buffer / guardrails</span>
                <span className="text-gray-500 text-xs">{money(reservaUsd)}</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-1 mt-2">
                <div className="h-1 rounded-full bg-gray-500" style={{ width: `${Math.min(100, reservaPct)}%` }} />
              </div>
            </div>

            {(() => {
              const totalPct = sectors.reduce((sum, sector) => sum + f(sector.pct_asignado), 0);
              const disponiblePct = Math.max(0, round(100 - totalPct - reservaPct, 1));
              const disponibleUsd = round(capitalDesplegable * disponiblePct / 100, 2);
              if (disponiblePct <= 0) return null;
              return (
                <div className="bg-blue-900/30 border border-blue-800 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-400" />
                      <span className="text-xs text-blue-300 font-medium">Disponible</span>
                    </div>
                    <span className="text-xs font-bold text-blue-400">{disponiblePct}%</span>
                  </div>
                  <p className="text-lg font-bold text-blue-400">{money(disponibleUsd)}</p>
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-600 text-xs">Sin desplegar</span>
                    <span className="text-gray-500 text-xs">idle</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-1 mt-2">
                    <div className="h-1 rounded-full bg-blue-500" style={{ width: `${disponiblePct}%` }} />
                  </div>
                </div>
              );
            })()}
          </div>
        </Card>

        <Card title="Trades (Ledger Completo)">
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2">
              <label className="text-gray-500 text-xs">Desde:</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(event) => { setDateFrom(event.target.value); setTradesDirty(true); }}
                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-gray-500 text-xs">Hasta:</label>
              <input
                type="date"
                value={dateTo}
                onChange={(event) => { setDateTo(event.target.value); setTradesDirty(true); }}
                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
              />
            </div>
            <select
              value={filterStrategy}
              onChange={(event) => { setFilterStrategy(event.target.value); setTradesDirty(true); }}
              className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
            >
              <option value="">Todos los sectores</option>
              {Object.entries(SECTOR_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <button
              onClick={() => fetchTrades(dateFrom, dateTo, filterStrategy)}
              className="bg-blue-600 hover:bg-blue-500 text-white text-xs px-3 py-1 rounded transition-colors"
            >
              Buscar
            </button>
            <button
              onClick={() => {
                setDateFrom("");
                setDateTo("");
                setFilterStrategy("");
                fetchTrades("", "", "");
              }}
              className="bg-gray-700 hover:bg-gray-600 text-white text-xs px-3 py-1 rounded transition-colors"
            >
              Limpiar
            </button>
            {tradesDirty ? <span className="text-yellow-500 text-xs self-center">Pulsa Buscar para actualizar</span> : null}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs border-b border-gray-800">
                  <th className="text-left pb-2">Sector</th>
                  <th className="text-left pb-2">Mercado</th>
                  <th className="text-right pb-2">Spread</th>
                  <th className="text-right pb-2">Size</th>
                  <th className="text-right pb-2">PnL</th>
                  <th className="text-right pb-2">Estado</th>
                  <th className="text-right pb-2">Modo</th>
                  <th className="text-right pb-2">Hora EC</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((trade, index) => (
                  <tr key={index} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="py-2 text-xs">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: SECTOR_COLORS[trade.strategy] || "#6b7280" }} />
                        <span className="text-gray-400">{SECTOR_LABELS[trade.strategy] || trade.strategy}</span>
                      </span>
                    </td>
                    <td className="py-2 text-xs text-gray-300 max-w-xs truncate">{trade.question}</td>
                    <td className="py-2 text-right text-xs font-medium text-yellow-400">{f(trade.spread_pct).toFixed(2)}%</td>
                    <td className="py-2 text-right text-xs text-gray-400">{money(trade.size_usd)}</td>
                    <td className={`py-2 text-right text-xs font-medium ${trade.pnl > 0 ? "text-green-400" : trade.pnl < 0 ? "text-red-400" : "text-gray-600"}`}>
                      {trade.pnl !== 0 ? money(trade.pnl) : <span className="text-gray-700">Abierto</span>}
                    </td>
                    <td className={`py-2 text-right text-xs ${STATUS_COLORS[trade.status] || "text-gray-500"}`}>{trade.status}</td>
                    <td className="py-2 text-right text-xs text-gray-500">{(trade.execution_mode || "paper").toUpperCase()}</td>
                    <td className="py-2 text-right text-xs text-gray-600">{formatEcTradeTime(trade.detected_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {trades.length === 0 ? <p className="text-center text-gray-600 text-sm py-6">Sin trades en el rango seleccionado</p> : null}
          </div>
        </Card>

        <p className="text-center text-gray-700 text-xs mt-6">Billuzo © 2026 - Auto-refresh 30s</p>
      </div>
    </div>
  );
}
